import React, { useState } from 'react'
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material'
import { Icon } from '@iconify/react'
import { useAuth } from '@/src/hooks/useAuth'

interface IframeLogoutButtonProps {
  position?: 'bottom-right' | 'bottom-left'
}

const IframeLogoutButton: React.FC<IframeLogoutButtonProps> = ({ position = 'bottom-right' }) => {
  const { logout, user } = useAuth()
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Only show if user is logged in
  if (!user) {
    return null
  }

  const handleLogout = async () => {
    try {
      setConfirmOpen(false)
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const getPositionStyles = () => {
    switch (position) {
      case 'bottom-right':
        return {
          position: 'fixed' as const,
          bottom: '0',
          right: '0',
          zIndex: 9999
        }
      case 'bottom-left':
        return {
          position: 'fixed' as const,
          bottom: '0',
          left: '0',
          zIndex: 9999
        }
      default:
        return {
          position: 'fixed' as const,
          bottom: '0',
          right: '0',
          zIndex: 9999
        }
    }
  }

  return (
    <>
      {/* Minimal Logout Button */}
      <Box sx={getPositionStyles()}>
        <Button
          variant='contained'
          size='small'
          onClick={() => setConfirmOpen(true)}
          sx={{
            backgroundColor: '#dc3545',
            color: 'white',
            borderRadius: '6px 6px 0 0',
            padding: '2px 8px',
            fontSize: '0.7rem',
            minWidth: 'auto',
            height: '24px',
            boxShadow: '0 -1px 6px rgba(0, 0, 0, 0.15)',
            '&:hover': {
              backgroundColor: '#c82333',
              boxShadow: '0 -1px 8px rgba(0, 0, 0, 0.25)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <Icon icon='mdi:logout' fontSize='12px' style={{ marginRight: '3px' }} />
          Logout
        </Button>
      </Box>

      {/* Simple Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth='xs'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant='h6' sx={{ color: '#495057' }}>
            Confirm Logout
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant='body1' sx={{ color: '#6c757d' }}>
            Are you sure you want to logout?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
          <Button
            variant='outlined'
            onClick={() => setConfirmOpen(false)}
            sx={{
              borderColor: '#dee2e6',
              color: '#6c757d',
              minWidth: 80
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleLogout}
            sx={{
              backgroundColor: '#dc3545',
              minWidth: 80,
              '&:hover': {
                backgroundColor: '#c82333'
              }
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default IframeLogoutButton
